// Hand-maintained mirror of marabot-api src/account-health/account-health-admin.service.ts;
// keep the two in step.

export type AccountHealthTier = "healthy" | "watch" | "at-risk";

export const TIER_LABELS: Record<AccountHealthTier, string> = {
    "at-risk": "At Risk",
    watch: "Watch",
    healthy: "Healthy",
};

export const TIER_DOT: Record<AccountHealthTier, string> = {
    "at-risk": "bg-[#d50000]",
    watch: "bg-secondary",
    healthy: "bg-[#43a047]",
};

export interface AccountHealthTrailPoint {
    day: string; // "YYYY-MM-DD"
    tier: AccountHealthTier;
}

export interface AccountHealthRow {
    userId: string;
    name: string;
    email: string;
    accountName: string | null;
    plan: string | null;
    renewsAt: string | null;
    tier: AccountHealthTier;
    previousTier: AccountHealthTier | null;
    score: number;
    // The first night of the current run in this tier, "YYYY-MM-DD".
    since: string;
    // The exact sentences the alert and digest emails use.
    reasons: string[];
    notes: string[];
    opportunities: string[];
    flags: string[];
    suggestedPlay: string | null;
    alertSentAt: string | null;
    // The snapshots inside the 14 calendar nights ending on `day`, oldest
    // first; a night with no snapshot is simply absent.
    trail: AccountHealthTrailPoint[];
}

export interface AccountHealthOverview {
    // null when no night has a snapshot yet.
    day: string | null;
    computedAt: string | null;
    // false = shadow mode: the digest goes out, the per-account alerts do not.
    alertsEnabled: boolean;
    counts: { atRisk: number; watch: number; healthy: number; planFull: number };
    // Posts waiting for the writer across all accounts, live.
    waitingForWriter?: number;
    rows: AccountHealthRow[];
}

// The page's buckets: the three tiers, the digest's own plan-full section
// (an upgrade conversation, whatever the tier), and everything.
export type AccountHealthFilter = AccountHealthTier | "plan-full" | "all";

export const FILTERS: AccountHealthFilter[] = ["at-risk", "watch", "healthy", "plan-full", "all"];

export const FILTER_LABELS: Record<AccountHealthFilter, string> = {
    "at-risk": "At Risk",
    watch: "Watch",
    healthy: "Healthy",
    "plan-full": "Plan full",
    all: "All",
};

export const PLAN_FULL_FLAG = "plan-full";

export const inBucket = (row: AccountHealthRow, filter: AccountHealthFilter): boolean => {
    if (filter === "all") return true;
    if (filter === "plan-full") return row.flags.includes(PLAN_FULL_FLAG);
    return row.tier === filter;
};

// The digest's clock: the nightly check runs at 00:45 America/New_York.
export const formatEasternDay = (iso: string): string =>
    new Date(iso).toLocaleDateString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
    });

export const formatEastern = (iso: string): string =>
    `${new Date(iso).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    })} ET`;
